import { FC, useState, useEffect, MouseEventHandler, ReactNode } from 'react'
import { Box, Typography } from '@mui/material'
import { Spinner } from 'src/components/MUIBased/icons'
import { HorizontalDivider } from 'src/components/MUIBased/dividers'

interface DetailRowProps {
    title: string
    isLoading?: boolean
    onEdit?: () => void
    onSave?: () => void
    onCancel?: () => void
    defaultEditValue?: boolean
    children: ReactNode
}

// helper component for each account-details row
export const DetailRow: FC<DetailRowProps> = ({
    title,
    isLoading,
    onEdit,
    onSave,
    onCancel,
    defaultEditValue,
    children
}) => {
    const [toggleEdit, setToggleEdit] = useState(!defaultEditValue)

    useEffect(() => {
        setToggleEdit(!defaultEditValue)
    }, [defaultEditValue])

    const onClickEdit = () => {
        setToggleEdit(!toggleEdit)
        onEdit && onEdit()
    }

    const onClickSave = () => {
        setToggleEdit(!toggleEdit)
        onSave && onSave()
    }

    const onClickCancel = () => {
        setToggleEdit(!toggleEdit)
        onCancel && onCancel()
    }

    const EditableRighHandSideButtons = () => {
        if (toggleEdit && onEdit) {
            return (
                <AccountDetailActionButton onClick={onClickEdit} text="Edit" />
            )
        }

        if (!toggleEdit) {
            return (
                <Box>
                    {onCancel && (
                        <AccountDetailActionButton
                            onClick={onClickCancel}
                            text="Cancel"
                        />
                    )}
                    {onSave && (
                        <AccountDetailActionButton
                            onClick={onClickSave}
                            text="Save"
                        />
                    )}
                </Box>
            )
        }
    }

    return (
        <>
            <HorizontalDivider />
            <Box
                sx={
                    onEdit && !defaultEditValue
                        ? { '& *:hover': { cursor: 'pointer' } }
                        : {}
                }
                onClick={() => onEdit && !defaultEditValue && onClickEdit()}
                role="button"
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ pb: '4px' }}>
                        {title}
                    </Typography>
                    {EditableRighHandSideButtons()}
                </Box>
                {isLoading ? <Spinner /> : children}
            </Box>
        </>
    )
}

interface AccountDetailActionButtonProps {
    text: string
    onClick: MouseEventHandler
}

const AccountDetailActionButton = ({
    text,
    onClick
}: AccountDetailActionButtonProps) => {
    return (
        <a
            href="#"
            onClick={onClick}
            style={{
                textDecoration: 'underline',
                fontSize: '14px',
                marginLeft: '8px'
            }}
        >
            {text}
        </a>
    )
}

export default DetailRow
